import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";
import {
    defaultEmptyLocatorDataString,
    locatorWeeks,
    myAxios,
    MyDate,
    weeknum,
} from "src/components/common/functions";

export type LocatorWeekData = {
    form: any[];
    emergency?: any[];
    status?: string;
    submitTime?: string;
};

export type LocatorListRow = {
    id: string;
    email: string;
    userName: string;
    lanId?: string;
    outside: string;
    hireDate: string;
    quitDate?: string;
    outsideTimeCards?: string;
    weekData: LocatorWeekData;
};

export type Division = {
    division: string;
    supervisorName?: string;
    supervisorEmail?: string;
    role?: string;
};

async function fetchDivisions(): Promise<Division[]> {
    const res = await myAxios({ fileName: "outside/divisions/readDivisions.php" });
    return Array.isArray(res?.data) ? res.data : [];
}

function parseWeekData(raw: unknown): LocatorWeekData {
    if (typeof raw === "string" && raw) {
        try {
            return JSON.parse(raw);
        } catch {
            return JSON.parse(defaultEmptyLocatorDataString);
        }
    }
    if (raw && typeof raw === "object") return raw as LocatorWeekData;
    return JSON.parse(defaultEmptyLocatorDataString);
}

export async function allLocatorListLoader({ params }: LoaderFunctionArgs) {
    const fallbackWeek = weeknum(new MyDate().s());
    const selectedWeek = params.week && locatorWeeks.includes(params.week) ? params.week : fallbackWeek;
    if (params.week !== selectedWeek) {
        throw redirect(`/OutsideStatus/${selectedWeek}`);
    }

    const formData = new FormData();
    formData.append("week", selectedWeek);
    const [rowsRes, divisions] = await Promise.all([
        myAxios({ fileName: "outside/locator/readTimeCards.php", formData }),
        fetchDivisions(),
    ]);
    if (!rowsRes) throw redirect("/");

    const rows: LocatorListRow[] = Array.isArray(rowsRes.data) ? rowsRes.data.map((row: any) => ({
        id: String(row.id ?? ""),
        email: row.email ?? "",
        userName: row.userName ?? "",
        lanId: row.lanId ?? "",
        outside: row.outside ?? "",
        hireDate: row.hireDate ?? "",
        quitDate: row.quitDate ?? "",
        outsideTimeCards: row.outsideTimeCards ?? "",
        weekData: parseWeekData(row[selectedWeek]),
    })) : [];

    return { week: selectedWeek, rows, divisions };
}

export async function allLocatorDetailLoader({ params }: LoaderFunctionArgs) {
    const fallbackWeek = weeknum(new MyDate().s());
    const selectedWeek = params.week && locatorWeeks.includes(params.week) ? params.week : fallbackWeek;
    const userId = params.userId ?? "";
    if (!userId) throw redirect(`/OutsideStatus/${selectedWeek}`);
    if (params.week !== selectedWeek) {
        throw redirect(`/OutsideStatus/${selectedWeek}/${userId}`);
    }

    const formData = new FormData();
    formData.append("week", selectedWeek);
    formData.append("userId", userId);
    const [rowRes, divisions] = await Promise.all([
        myAxios({ fileName: "outside/locator/readTimeCard.php", formData }),
        fetchDivisions(),
    ]);
    if (!rowRes?.data) throw redirect(`/OutsideStatus/${selectedWeek}`);

    const row = rowRes.data;
    return {
        week: selectedWeek,
        locator: {
            id: String(row.id ?? userId),
            email: row.email ?? "",
            userName: row.userName ?? "",
            lanId: row.lanId ?? "",
            outside: row.outside ?? "",
            hireDate: row.hireDate ?? "",
            quitDate: row.quitDate ?? "",
            outsideTimeCards: row.outsideTimeCards ?? "",
        },
        weekData: parseWeekData(row[selectedWeek]),
        divisions,
    };
}

export async function allLocatorWithdrawAction({ params }: ActionFunctionArgs) {
    const formData = new FormData();
    formData.append("userId", params.userId ?? "");
    formData.append("wk", params.week ?? "");
    const res = await myAxios({ fileName: "outside/locator/withdrawWeek.php", formData });
    if (!res) return null;
    return redirect(`/OutsideStatus/${params.week}/${params.userId}`);
}

export async function allLocatorAddEmergencyAction({ params }: ActionFunctionArgs) {
    const formData = new FormData();
    formData.append("userId", params.userId ?? "");
    formData.append("wk", params.week ?? "");
    const res = await myAxios({ fileName: "outside/locator/emergency/add.php", formData });
    if (!res) return null;
    return redirect(`/OutsideStatus/${params.week}/${params.userId}`);
}

export async function allLocatorDeleteEmergencyAction({ params, request }: ActionFunctionArgs) {
    const requestForm = await request.formData();
    const formData = new FormData();
    formData.append("userId", params.userId ?? "");
    formData.append("wk", params.week ?? "");
    formData.append("index", String(requestForm.get("index") ?? ""));
    const res = await myAxios({ fileName: "outside/locator/emergency/delete.php", formData });
    if (!res) return null;
    return redirect(`/OutsideStatus/${params.week}/${params.userId}`);
}

export async function allLocatorSwitchDayAction({ params, request }: ActionFunctionArgs) {
    const requestForm = await request.formData();
    const formData = new FormData();
    formData.append("userId", params.userId ?? "");
    formData.append("wk", params.week ?? "");
    formData.append("fromIndex", String(requestForm.get("fromIndex") ?? ""));
    formData.append("toIndex", String(requestForm.get("toIndex") ?? ""));
    const res = await myAxios({ fileName: "outside/locator/switchDay.php", formData });
    if (!res) return null;
    return redirect(`/OutsideStatus/${params.week}/${params.userId}`);
}

export async function allLocatorSwitchWeekAction({ params, request }: ActionFunctionArgs) {
    const requestForm = await request.formData();
    const formData = new FormData();
    formData.append("userId", params.userId ?? "");
    formData.append("fromWk", params.week ?? "");
    formData.append("toWk", String(requestForm.get("toWk") ?? ""));
    const res = await myAxios({ fileName: "outside/locator/switchWeek.php", formData });
    if (!res) return null;
    return redirect(`/OutsideStatus/${params.week}/${params.userId}`);
}
